import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  SimpleGrid,
  useToast,
  Flex,
  Button,
  HStack,
  Input,
  Select,
  Text,
} from '@chakra-ui/react';
import DynamicTable from '../../../components/table/DynamicTable.js';
import { columnsDataStudies } from './variables/columnsDataStudies.js';
import { getData, postData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import CreateModal from '../../../components/examInscription/CreateModal.js';
import ScheduleExamModal from '../../../components/examInscription/ScheduleExamModal.jsx';
import ConfirmModal from '../../../components/modal/ConfirmModal.js';

const initialFormData = {
  id: '',
  description: '',
  studyTypeId: '',
  studyTypeDescription: '',
  createdBy: 0,
  updatedBy: 0,
  createdDate: '',
  updatedDate: '',
  enabled: false,
};

// Filtros soportados por GET /exam/schedule
// (examPeriodId, subjectId, status, startDate, endDate).
// Los inputs de abajo sólo los arman como query params y dejan que el
// backend aplique el filtro real en SQL.
const initialFilters = {
  examPeriodId: '',
  subjectId: '',
  status: '',
  startDate: '',
  endDate: '',
};

export default function Settings() {
  const { searchQuery } = useSearch();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    isOpen: false,
    confirmModalOpen: false,
  });
  // Modal "Nuevo examen": crea exam_schedule (sin período visible,
  // requerimiento 7).
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const [formData, setFormData] = useState(initialFormData);

  // Catálogos de filtros (combos de período y materia). Se cargan una vez.
  const [periods, setPeriods] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState(initialFilters);

  // Header de "última inscripción": la inscripción a examen más reciente
  // que haya en el sistema. En un futuro, al haber login de alumno, se
  // debería filtrar por el studentId del token.
  const [lastInscription, setLastInscription] = useState(null);

  const rowsPerPage = 10;
  const toast = useToast();

  const filterQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (searchQuery) params.set('search', searchQuery);
    if (filters.examPeriodId) params.set('examPeriodId', filters.examPeriodId);
    if (filters.subjectId) params.set('subjectId', filters.subjectId);
    if (filters.status) params.set('status', filters.status);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    return params.toString();
  }, [page, searchQuery, filters]);

  const latestRequestRef = useRef(0);

  const getStudies = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      const response = await getData(`/exam/schedule?${filterQuery}`);
      if (requestId !== latestRequestRef.current) return;
      const rows = response?.payload?.exams || [];

      const mapped = rows.map((r) => ({
        id: r.id,
        study: { description: r.study_name || '-' },
        studyPlan: { description: r.period_name || '-' },
        subject: { description: r.subject_name || '-' },
        examDateTime:
          r.exam_date && r.exam_time
            ? `${r.exam_date}T${r.exam_time}`
            : r.exam_date || null,
      }));

      setData(mapped);
      setTotalPages(
        Math.max(1, Math.ceil((response?.payload?.total || mapped.length) / rowsPerPage)),
      );
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      setData([]);
      setTotalPages(0);
      toast({
        title: 'Error al obtener exámenes programados',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Catálogos para combos. Se cargan una sola vez.
  const loadFilterCatalogs = async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        getData('/exam/periods?page=1&limit=200'),
        getData('/subject?page=1&limit=500'),
      ]);
      setPeriods(pRes?.payload?.periods || pRes?.payload || []);
      setSubjects(sRes?.payload?.subjects || sRes?.payload?.data || []);
    } catch (e) {
      // Silencioso: los combos sólo son UX.
    }
  };

  const loadLastInscription = async () => {
    try {
      // Pedimos la primer página de inscripciones; como /exam/inscriptions
      // las devuelve ordenadas por fecha desc, la primera fila es la
      // última inscripción registrada.
      const res = await getData('/exam/inscriptions?page=1&limit=1');
      const row = (res?.payload?.inscriptions || [])[0] || null;
      setLastInscription(row);
    } catch (e) {
      // Silencioso: no bloquea la grilla.
    }
  };

  const createStudy = async () => {
    try {
      const newStudy = {
        description: formData.description,
        studyTypeId: formData.studyTypeId,
      };
      const response = await postData('/study', newStudy);
      if (response?.payload) {
        toast({
          title: 'Estudio creado con éxito',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        closeModal();
      }
    } catch (error) {
      toast({
        title: 'Error al crear estudio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadFilterCatalogs();
    loadLastInscription();
  }, []);

  useEffect(() => {
    getStudies();
  }, [filterQuery]);

  const resetForm = () => setFormData(initialFormData);

  const openModal = () => setModalState({ isOpen: true, confirmModalOpen: false });
  const closeModal = () => {
    setModalState({ isOpen: false, confirmModalOpen: false });
    resetForm();
  };
  const showConfirmModal = () => setModalState({ isOpen: true, confirmModalOpen: true });
  const hiddeConfirmModal = () => setModalState({ isOpen: true, confirmModalOpen: false });

  const handleConfirm = async () => {
    await createStudy();
    await getStudies();
  };

  const updateFilter = (key, value) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setPage(0);
    setFilters(initialFilters);
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Header: última inscripción registrada */}
      {lastInscription && (
        <Box bg="white" p={4} borderRadius="md" boxShadow="sm" mb={4}>
          <Text fontSize="sm" color="gray.500">
            Última inscripción registrada
          </Text>
          <Text fontWeight="bold">
            {[lastInscription.student_name, lastInscription.student_last_name]
              .filter(Boolean)
              .join(' ') || 'Alumno'}{' '}
            → {lastInscription.subject_name || 'Examen'}{' '}
            {lastInscription.exam_date
              ? `(${new Date(lastInscription.exam_date).toLocaleDateString('es-ES')})`
              : ''}
          </Text>
          <Text fontSize="xs" color="gray.500">
            Inscripción:{' '}
            {lastInscription.inscription_date
              ? new Date(lastInscription.inscription_date).toLocaleString('es-ES')
              : '-'}
          </Text>
        </Box>
      )}

      {/* Filtros */}
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm" mb={4}>
        <HStack spacing={3} flexWrap="wrap">
          <Select
            placeholder="Período"
            size="sm"
            maxW="200px"
            value={filters.examPeriodId}
            onChange={(e) => updateFilter('examPeriodId', e.target.value)}
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || `${p.year}-${p.period_number ?? p.periodNumber}`}
              </option>
            ))}
          </Select>
          <Select
            placeholder="Materia"
            size="sm"
            maxW="240px"
            value={filters.subjectId}
            onChange={(e) => updateFilter('subjectId', e.target.value)}
          >
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.description}
              </option>
            ))}
          </Select>
          <Select
            placeholder="Estado"
            size="sm"
            maxW="180px"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="SCHEDULED">SCHEDULED</option>
            <option value="IN_PROGRESS">IN_PROGRESS</option>
            <option value="COMPLETED">COMPLETED</option>
            <option value="CANCELLED">CANCELLED</option>
          </Select>
          <Input
            type="date"
            size="sm"
            maxW="180px"
            value={filters.startDate}
            onChange={(e) => updateFilter('startDate', e.target.value)}
          />
          <Input
            type="date"
            size="sm"
            maxW="180px"
            value={filters.endDate}
            onChange={(e) => updateFilter('endDate', e.target.value)}
          />
          <Button size="sm" variant="outline" onClick={clearFilters}>
            Limpiar
          </Button>
        </HStack>
      </Box>

      <Flex justify="flex-end" mb={4} gap={2}>
        <Button
          variant="outline"
          colorScheme="blue"
          onClick={() => setScheduleModalOpen(true)}
        >
          Nuevo examen
        </Button>
        <Button colorScheme="blue" onClick={() => openModal()}>
          Nueva inscripción
        </Button>
      </Flex>
      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <DynamicTable
          columnsData={columnsDataStudies}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          openModal={openModal}
        />
      </SimpleGrid>

      <CreateModal
        isOpen={modalState.isOpen}
        onClose={showConfirmModal}
        formData={formData}
        setFormData={setFormData}
        onConfirm={handleConfirm}
      />

      <ConfirmModal
        isOpen={modalState.confirmModalOpen}
        onClose={hiddeConfirmModal}
        onConfirm={closeModal}
        header="Confirmación de Cancelación"
        message="¿Estás seguro de que deseas cancelar? Todos los datos no guardados se perderán."
      />

      <ScheduleExamModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        onSuccess={() => {
          setScheduleModalOpen(false);
          getStudies();
        }}
      />
    </Box>
  );
}
